import {
  MapViewerFlagsAPI,
  MapViewerLoadMapOptions,
  MapViewerLoadObject,
  MapViewerNode,
  MapViewerPluginsList,
  MapViewerStyles,
  MapViewerStylesState,
  Viewer3dLoadView3dOptions,
} from '@3ddv/dvm-internal';
import {
  MapViewerInitOptions,
  MapViewerService,
  Viewer3dInitOptions,
  Viewer3dService,
} from '@3ddv/ngx-dvm-internal';
import {
  Injectable,
  OnDestroy,
  Signal,
  ViewContainerRef,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Placement, createPopper } from '@popperjs/core';
import {
  Observable,
  Subscription,
  firstValueFrom,
  from,
  tap,
  timer,
} from 'rxjs';
import { DvmData, DvmStyles } from '../models/configuration.model';
import { BG_MAP_VIEWER_TOKEN } from '../tokens/bg-map-viewer.token';
import { MINIMAP_TOKEN } from '../tokens/minimap.token';
import { ConfigurationService } from './configuration.service';
import { DemoDataService } from './demo-data.service';
import { UtilitiesService } from './utilities.service';

@Injectable({
  providedIn: 'root',
})
export class DvmService implements OnDestroy {
  /**
   * DVM SERVICES
   */
  private utilitiesService = inject(UtilitiesService);
  public readonly viewerService = inject(MapViewerService);
  public readonly configurationService = inject(ConfigurationService);
  /**
   * `<dvm-map-viewer [service]="dvmService.minimapService">` (viewer.component.html)
   * binds this on first render, so it must already hold a valid instance — the
   * component's `service` input is input-only (@3ddv/ngx-dvm-internal 5.x) and no
   * longer exposes its own internally-created instance back to the parent, so this
   * can't be assigned later from a ViewChild the way it used to be.
   */
  public readonly minimapService = inject(MINIMAP_TOKEN);
  /** Same reasoning as `minimapService`, for `<dvm-viewer3d [service]="dvmService.viewer3dService">`. */
  public readonly viewer3dService = inject(Viewer3dService);
  /** Headless viewer used only to silently query a section's `related` map data — see `getRelatedMapsForSection`. */
  private readonly bgViewerService = inject(BG_MAP_VIEWER_TOKEN);
  /**
   * Per-venue cache for `getRelatedMapsForSection`, keyed by `venue_id:sectionId` (this
   * service is `providedIn: 'root'`, so plain `sectionId` keys could otherwise return a
   * stale result after navigating to a different event/venue in the same SPA session).
   * Avoids re-hitting the background viewer for a section already resolved.
   */
  private relatedMapsQueue: Promise<void> = Promise.resolve();
  private readonly relatedMapsCache = new Map<
    string,
    Record<string, { map_id: string }> | null
  >();

  /* Other Services */
  private demoDataService = inject(DemoDataService);

  /**
   * INIT OPTIONS
   */
  private mapViewerInitOptions: MapViewerInitOptions = { plugins: [] };
  private viewer3dInitOptions: Viewer3dInitOptions = {};

  /**
   * LOAD OPTIONS
   */
  public mapViewerLoadOptions: MapViewerLoadMapOptions = {
    venue_id: '',
    map_id: '',
  };
  public minimapLoadOptions: MapViewerLoadMapOptions = {
    venue_id: '',
    map_id: '',
  };
  private viewer3dLoadOptions: Viewer3dLoadView3dOptions = {
    venue_id: '',
    view_id: '',
  };

  /**
   * STYLES
   */
  private customStyles!: DvmStyles;

  /**
   * MISC
   */
  private readonly baseAvailability: string[] = [];
  isFirstSeatMapLoad = true;
  isMultiSelectTooltipVisible: boolean = false;
  popoverPlacement: ViewContainerRef;
  viewerSubscriptions: Subscription[] = [];
  private sectionsMmcIdToTdc: Record<string, any> = {};
  private sectionsTdcToMmc: Record<string, any> = {};

  /**
   * UTILITIES
   */
  private _isTopView: WritableSignal<boolean> = signal(true);
  private _is3dView: WritableSignal<boolean> = signal(false);
  private _showMinimap: WritableSignal<boolean> = signal(false);
  public readonly isTopView: Signal<boolean> = computed(() =>
    this._isTopView()
  );
  public readonly is3dView: Signal<boolean> = computed(() => this._is3dView());
  public readonly showMinimap: Signal<boolean> = computed(() =>
    this._showMinimap()
      ? !this.is3dView() && !this.isTopView()
      : this._showMinimap()
  );

  /**
   * Creates a popover for a given node and tooltip element.
   *
   * @param node - The MapViewerNode for which the popover is created.
   * @param tooltip - The HTML element that serves as the tooltip.
   * @param elementDOMId - The DOM ID of the element used for positioning reference.
   *
   * @remarks
   * This method calculates the center position of the node in the DOM, determines the appropriate placement
   * (top or bottom) based on the node's vertical center, and creates a popper with specified offset and flip options.
   * The tooltip is then displayed by setting the `data-show` attribute.
   *
   * @author Jonathan Alvarado
   */
  public createPopover(
    node: MapViewerNode,
    tooltip: HTMLElement,
    options: Parameters<typeof createPopper>[2]
  ) {
    const popper = createPopper(node, tooltip, options);

    tooltip.setAttribute('data-show', '');

    return popper;
  }

  /**
   * Determines the placement of a node relative to a DOM element.
   *
   * @param node - The MapViewerNode object containing the node's bounding box (aabb).
   * @param elementDOMId - The ID of the DOM element to compare against.
   * @returns The placement of the node ('top' or 'bottom') relative to the vertical center of the DOM element.
   *
   * @author Jonathan Alvarado
   */
  public getPlacement(node: MapViewerNode, elementDOMId: string): Placement {
    const nodeDomCenter = this.viewerService.fromSceneToDom([
      node.aabb![0],
      node.aabb![1],
    ]);
    const nodeHalfHeight = node.aabb![3] / 2;
    const nodeVerticalCenter = nodeDomCenter![1] + nodeHalfHeight;
    const element = document.getElementById(elementDOMId) as HTMLElement;
    return nodeVerticalCenter > element.offsetHeight / 2 ? 'top' : 'bottom';
  }

  /**
   * Calculates the offset for a given MapViewerNode based on its type.
   *
   * @param node - The MapViewerNode for which to calculate the offset.
   * @returns A tuple containing the x and y offsets.
   *
   * @author Jonathan Alvarado
   */
  public getOffset(node: MapViewerNode): [number, number] {
    return node.type === 'seat' ? [0, 6] : [0, -10];
  }

  /**
   * UTILITIES
   */
  private formatRgb: (rgbString: string) => string =
    this.utilitiesService.formatRgb;

  /**
   *
   * @param {DvmData} data
   * @param {MapViewerPluginsList[]} plugins
   * @param {MapViewerFlagsAPI} flags
   * @param {DvmStyles} customStyles
   * @param {string} customClient
   * @param {string[]} baseAvailability
   * @param {boolean} debugMode
   */
  public initMapViewer(
    data: DvmData,
    plugins?: MapViewerPluginsList[] | null,
    flags?: MapViewerFlagsAPI | null,
    customStyles?: DvmStyles | null,
    customClient?: string | null,
    baseAvailability?: string[] | null,
    debugMode?: boolean
  ): void {
    // Si no esta inicializado, lo inicializamos
    if (!this.viewerService.isInitialized()) {
      // Asignamos los plugins
      plugins ? this.setPlugins(plugins) : false;

      // Asignamos los estilos
      customStyles ? (this.customStyles = customStyles) : false;

      // Asignamos el cliente
      customClient ? this.setClient(customClient) : this.setClient();

      // Inicializamos el viewer
      const subscription = this.viewerService
        .initialize(this.mapViewerInitOptions)
        .subscribe(() => {
          // Asignamos las opciones de carga
          this.setViewerLoadOptions(data);

          if (baseAvailability) {
            this.setBaseAvailability(baseAvailability);
          }

          // Cargamos el mapa
          const subscription = this.loadMap(
            this.mapViewerLoadOptions
          ).subscribe(() => {
            // Seteamos los flags
            if (flags) {
              this.setFlags(flags);
            }

            if (baseAvailability && !this.demoDataService.isDemoActive()) {
              this.setMapAvailability(undefined, baseAvailability);
            }

            // Modo debug
            if (debugMode) {
              (window as any)['viewer'] = this.viewerService;
            }
          });

          this.viewerSubscriptions.push(subscription);
        });

      this.viewerSubscriptions.push(subscription);
    } else {
      // Asignamos las opciones de carga
      this.setViewerLoadOptions(data);

      // Asignamos la disponibilidad base
      if (baseAvailability) {
        this.setBaseAvailability(baseAvailability);
      }

      // Cargamos el mapa
      const subscription = this.loadMap(this.mapViewerLoadOptions).subscribe(
        () => {
          // Seteamos los flags
          if (flags) {
            this.setFlags(flags);
          }

          // Seteamos la disponibilidad
          if (baseAvailability && !this.demoDataService.isDemoActive()) {
            this.setMapAvailability(undefined, this.baseAvailability);
          }

          // Modo debug
          if (debugMode) {
            (window as any)['viewer'] = this.viewerService;
          }
        }
      );

      this.viewerSubscriptions.push(subscription);
    }
  }

  public initViewer3d(venue_id?: string): void {
    if (!this.viewer3dService.isInitialized()) {
      const subscription = this.viewer3dService
        .initialize(this.viewer3dInitOptions)
        .subscribe(() => {
          this.setViewer3dLoadOptions(venue_id);
        });
      this.viewerSubscriptions.push(subscription);
    }
  }

  public initMinimap(dvmData: DvmData, debugMode?: boolean): void {
    if (!dvmData.miniMapId && !dvmData.miniMapEnabled) {
      console.log('No se ha configurado el minimapa');
      return;
    }

    this._showMinimap.update(x => !x);

    if (!this.minimapService.isInitialized()) {
      const options = Object.assign({}, this.mapViewerInitOptions);
      options.client_id = dvmData.clientId ?? 'tdc';

      const subscription = this.minimapService.initialize(options).subscribe({
        next: () => {
          if (debugMode) {
            (window as any)['minimap'] = this.minimapService;
          }

          this.setFlagsMini();
          this.setMinimapLoadOptions(dvmData);
          this.loadMinimap();
        },
        error: e => console.log(e),
      });
      this.viewerSubscriptions.push(subscription);
    }

    return;
  }

  public openSectionMap(): Observable<MapViewerLoadObject> {
    return this.loadMap();
  }

  public openSeatMap(sectionId: string): Observable<MapViewerLoadObject> {
    // Cargamos objeto de config del mapa y le reasignamos la propiedad map id
    const loadOptions: MapViewerLoadMapOptions = this.getMapLoadOptions();
    loadOptions.map_id = sectionId;

    return this.loadMap(loadOptions);
  }

  public setMapAvailability(type?: string, availability?: string[]): void {
    if (type && availability) {
      this.viewerService.setAvailability(type, availability);
    } else {
      this.viewerService
        .getTypesList()
        .forEach(t =>
          this.viewerService.setAvailability(
            type ?? t,
            availability ?? this.viewerService.getNodesByType(type ?? t)
          )
        );
    }
  }

  public getMapLoadOptions(): MapViewerLoadMapOptions {
    return Object.assign({}, this.mapViewerLoadOptions);
  }

  public getViewer3dLoadOptions(): Viewer3dLoadView3dOptions {
    return Object.assign({}, this.viewer3dLoadOptions);
  }

  /**
   * Initializes the headless background viewer used by `getRelatedMapsForSection`.
   * No `container` is passed — it never renders, it only holds map data in memory.
   * Safe to call multiple times; no-ops once initialized.
   *
   * Must explicitly request the `related` plugin here — it is NOT part of the main
   * viewer's own plugin list (`['best_nodes', 'selection_area']`, see `initComponent`
   * in viewer.component.ts), and `mapViewerInitOptions['plugins']` is not forwarded
   * to this second viewer instance. Without it, `bgViewerService.related` stays
   * `undefined` forever and `getRelatedMapsForSection` silently resolves to `null`
   * for every section — no error, no visible symptom other than sdc-neighbors never
   * showing any arrows at all.
   */
  public async initViewerBg(): Promise<void> {
    if (this.bgViewerService.isInitialized()) {
      return;
    }

    try {
      await firstValueFrom(
        this.bgViewerService.initialize({
          client_id: this.mapViewerInitOptions['client_id'],
          plugins: ['related'],
        })
      );
    } catch (error) {
      console.error('[DVM] background viewer init error', error);
    }
  }

  /**
   * Loads a section silently on the background viewer and returns its related maps
   * (per the DVM `related` plugin).
   * Used by `sdc-neighbors` (via `NeighborsDvmService`) to resolve prev/next sections
   * without navigating the user's own, visible map.
   *
   * Lazily initializes the background viewer on first call rather than eagerly on
   * every select-seat page load: most visitors never drill into a section (the only
   * place `sdc-neighbors` renders, see `map-controls.component.html`'s
   * `@if (!isTopView() && !isView3d())`), so they'd otherwise pay for a second DVM
   * viewer bootstrap they never use. `initViewerBg()` is a no-op once initialized,
   * so this stays cheap on subsequent calls.
   */
  public getRelatedMapsForSection(
    sectionId: string
  ): Promise<Record<string, { map_id: string }> | null> {
    const loadOptions = this.getMapLoadOptions();
    const cacheKey = `${loadOptions.venue_id}:${sectionId}`;

    if (this.relatedMapsCache.has(cacheKey)) {
      return Promise.resolve(this.relatedMapsCache.get(cacheKey) ?? null);
    }

    // The background viewer holds one map at a time: a second `loadMap` cancels
    // the first. Chain the requests so each section loads after the previous one.
    const request = this.relatedMapsQueue.then(() =>
      this.loadRelatedMaps(sectionId, loadOptions, cacheKey)
    );
    this.relatedMapsQueue = request.then(
      () => undefined,
      () => undefined
    );
    return request;
  }

  private async loadRelatedMaps(
    sectionId: string,
    loadOptions: MapViewerLoadMapOptions,
    cacheKey: string
  ): Promise<Record<string, { map_id: string }> | null> {
    try {
      await this.initViewerBg();
      loadOptions.map_id = sectionId;
      await firstValueFrom(from(this.bgViewerService.loadMap(loadOptions)));
      const relatedMaps =
        this.bgViewerService.related?.getRelatedMaps() ?? null;
      this.relatedMapsCache.set(cacheKey, relatedMaps);
      return relatedMaps;
    } catch (error) {
      console.error('[DVM] related maps error', error);
      return null;
    }
  }

  public loadThumbnail(viewId: string): Observable<HTMLImageElement> {
    const loadOptions: Viewer3dLoadView3dOptions = {
      venue_id: this.mapViewerLoadOptions.venue_id,
      view_id: viewId,
    };

    return from(this.viewerService.getThumbnail(loadOptions));
  }

  public load3dView(viewId: string): void {
    if (this.viewer3dService.isLoaded()) {
      const viewLoaded = this.viewer3dService.getViewId();

      if (viewLoaded !== viewId) {
        this.viewer3dService.reset();
      }
    }

    const load3dOptions: Viewer3dLoadView3dOptions =
      this.getViewer3dLoadOptions();

    load3dOptions.view_id = viewId;

    const subscription = from(
      this.viewer3dService.loadView3d(load3dOptions)
    ).subscribe({
      next: () => {
        // Seteamos Flags
        this.setFlags3d();

        // Si is3dView es false,es que el viewer estaba cerrado, updateamos a false,
        // pero si ya estaba en true y estamos cargando de nuevo, seguimos mostrando el viewer
        !this.is3dView() ? this._is3dView.update(x => !x) : null;

        // Agregamos label position
        this.viewer3dService.interface.setPoweredByLabel({
          position: 'bottomright',
        });
      },
      error: (e: unknown) => console.log(e),
    });

    this.viewerSubscriptions.push(subscription);
  }

  public close3dView(): void {
    if (this.is3dView()) {
      this._is3dView.set(false);
    }
    return;
  }

  public translateSectionMmcId(mmcId: string): string | null {
    return this.sectionsMmcIdToTdc[mmcId] || null;
  }

  public translateSectionTdcId(tdcId: string): string | null {
    return this.sectionsTdcToMmc[tdcId] || null;
  }

  /**
   * INIT METHODS
   */
  private setViewerLoadOptions(data: DvmData): void {
    this.mapViewerLoadOptions.venue_id = data.venueId;
    this.mapViewerLoadOptions.map_id = data.mapId;
  }

  private setMinimapLoadOptions(data: DvmData): void {
    this.minimapLoadOptions.venue_id = data.venueId;
    this.minimapLoadOptions.map_id = data.miniMapId;
  }

  private setViewer3dLoadOptions(venue_id?: string): void {
    this.viewer3dLoadOptions.venue_id =
      venue_id ?? this.viewerService.getVenueId()!;
  }

  public loadMap(
    loadOptions?: MapViewerLoadMapOptions
  ): Observable<MapViewerLoadObject> {
    const mapId = loadOptions
      ? (loadOptions.map_id as string)
      : (this.mapViewerLoadOptions.map_id as string);

    const isTopView = this.checkIsTopView(mapId as string);
    this.zoomInMinimap(isTopView, mapId as string);
    this.showMultiSelecTooltip(isTopView);

    if (this.showMinimap()) {
      this._showMinimap.update(x => !x);
    }

    return from(
      this.viewerService.loadMap(loadOptions ?? this.mapViewerLoadOptions)
    ).pipe(
      tap(() => this.setFlags()),
      tap(() => this.applyStyles()),
      tap(() => this.updateSectionIdTranslation()),
      tap(() => this._showMinimap.update(x => !x)),
      tap(() => {
        if (this.demoDataService.isDemoActive()) {
          this.demoDataService.setDemoAvailability(this.viewerService);
        }
      })
    );
  }

  private zoomInMinimap(isTopView: boolean, sectionMapId: string): void {
    if (isTopView) {
      if (
        this.minimapService.isLoaded() &&
        this.minimapService.isInitialized()
      ) {
        this.minimapService.goTo([0, 0], 1, 100);
      }
    } else {
      if (
        !this.minimapService.isLoaded() ||
        !this.minimapService.isInitialized()
      ) {
        return;
      }

      const tdcId = this.translateSectionTdcId(sectionMapId);
      const nodeId = tdcId ?? sectionMapId;
      const selectedSectionNodes = this.minimapService.getNodesByState(
        'section',
        'selected'
      );
      this.minimapService.setState(selectedSectionNodes, 'unavailable', true);

      const timeoutId = setTimeout(() => {
        this.minimapService.setState(nodeId, 'selected', true);
        this.minimapService.goTo(tdcId ?? sectionMapId, 3, 1500);
        clearTimeout(timeoutId);
      }, 3000);
    }
  }

  private showMultiSelecTooltip(isTopView: boolean): void {
    if (isTopView) {
      this.isMultiSelectTooltipVisible = false;
    } else {
      this.isMultiSelectTooltipVisible = !!this.isFirstSeatMapLoad;
      const isFirstSeatMapLoad = this.isFirstSeatMapLoad;
      const multiSelectionToastSubscription = timer(10000).subscribe(() => {
        if (isFirstSeatMapLoad) {
          this.isMultiSelectTooltipVisible = false;
        }
        multiSelectionToastSubscription.unsubscribe();
      });
      this.viewerSubscriptions.push(multiSelectionToastSubscription);
    }
  }

  private loadMinimap() {
    const subscription = from(
      this.minimapService.loadMap(this.minimapLoadOptions)
    ).subscribe({
      next: () => {
        this.setFlagsMini();
        this._showMinimap.update(x => !x);
      },
      error: (e: unknown) => console.log(e),
    });

    this.viewerSubscriptions.push(subscription);
  }

  private setClient(client: string = 'tdc'): void {
    this.mapViewerInitOptions['client_id'] = client;
  }

  private setPlugins(plugins: MapViewerPluginsList[]): void {
    this.mapViewerInitOptions['plugins'] = plugins;
  }

  private setFlags(flags?: MapViewerFlagsAPI): void {
    // DEFAULT FLAGS
    if (!flags) {
      if (this.configurationService.configuration.dvmData.limitedZoom) {
        this.viewerService.flags.max_zoom_on_first_limit = true;
      }

      this.viewerService.flags.preserve_min_scaling_factor = true;
      this.viewerService.flags.fixed_aspect_ratio = false;
      this.viewerService.flags.automatic_selection = false;
    }
    // CUSTOM FLAGS
    else {
      Object.entries(flags).forEach(([key, value]) => {
        if (this.viewerService.flags.hasOwnProperty(key)) {
          (this.viewerService.flags as unknown as Record<string, unknown>)[
            key
          ] = value;
        }
      });
    }
  }

  private setFlagsMini(flags?: MapViewerFlagsAPI): void {
    // DEFAULT FLAGS
    if (!flags) {
      this.minimapService.flags.fixed_aspect_ratio = false;
      this.minimapService.flags.automatic_selection = false;
      this.minimapService.flags.automatic_hover = false;
      this.minimapService.flags.zooming = false;
      this.minimapService.flags.panning = false;
    }
    // CUSTOM FLAGS
    else {
      Object.entries(flags).forEach(([key, value]) => {
        if (this.minimapService.flags.hasOwnProperty(key)) {
          (this.minimapService.flags as unknown as Record<string, unknown>)[
            key
          ] = value;
        }
      });
    }
  }

  private setFlags3d(): void {
    this.viewer3dService.flags.fixed_aspect_ratio = false;
  }

  private applyStyles(): void {
    if (!this.customStyles) {
      return;
    }

    // DVM STYLES
    const styles: MapViewerStyles = this.viewerService.getStyles();

    // SECTION STYLES
    if (styles && styles[0]['section']) {
      const sectionAvailable: MapViewerStylesState = styles[0]['section'][
          'available'
        ]! as MapViewerStylesState,
        sectionSelected: MapViewerStylesState = styles[0]['section'][
          'selected'
        ]! as MapViewerStylesState;

      // AVAILABLE NORMAL
      sectionAvailable.normal.none.fillStyle = this.formatRgb(
        this.customStyles['section-available-normal-fill']
      );
      sectionAvailable.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['section-available-normal-stroke']
      );

      // AVAILABLE HOVER
      sectionAvailable.hover!.none.fillStyle = this.formatRgb(
        this.customStyles['section-available-hover-fill']
      );
      sectionAvailable.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-available-hover-stroke']
      );

      // SELECTED NORMAL
      sectionSelected.normal.none.fillStyle = this.formatRgb(
        this.customStyles['section-selected-normal-fill']
      );

      sectionSelected.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['section-selected-normal-stroke']
      );

      // SELECTED HOVER
      sectionSelected.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-selected-hover-stroke']
      );

      styles[0]['section']['available'] = sectionAvailable;
      styles[0]['section']['selected'] = sectionSelected;
    }

    // SECTION_NH STYLES
    if (styles && styles[0]['section_nh']) {
      const nhAvailable: MapViewerStylesState = styles[0]['section_nh'][
        'available'
      ]! as MapViewerStylesState;
      const nhUnavailable: MapViewerStylesState = styles[0]['section_nh'][
        'unavailable'
      ]! as MapViewerStylesState;

      const isTouchDevice =
        this.utilitiesService.isMobile || this.utilitiesService.isTablet;

      // AVAILABLE HOVER (same for both desktop and mobile)
      nhAvailable.hover!.none.fillStyle = this.formatRgb(
        this.customStyles['section-nh-available-hover-fill']
      );
      nhAvailable.hover!.none.fillOpacity = 0.3;
      nhAvailable.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-nh-available-hover-stroke']
      );
      nhAvailable.hover!.none.strokeOpacity = 1;

      // AVAILABLE NORMAL (mobile/tablet permanently show the desktop hover
      // styles, including opacity, instead of just the hover colors)
      if (isTouchDevice) {
        nhAvailable.normal.none = { ...nhAvailable.hover!.none };
      } else {
        nhAvailable.normal.none.fillStyle = 'transparent';
        nhAvailable.normal.none.strokeStyle = 'transparent';
      }

      // UNAVAILABLE NORMAL (uses customStyles, can be 'transparent')
      nhUnavailable.normal.none.fillStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-normal-fill']
      );
      nhUnavailable.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-normal-stroke']
      );

      // UNAVAILABLE HOVER (uses customStyles, can be 'transparent')
      nhUnavailable.hover!.none.fillStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-hover-fill']
      );
      nhUnavailable.hover!.none.strokeStyle = this.formatRgb(
        this.customStyles['section-nh-unavailable-hover-stroke']
      );

      styles[0]['section_nh']['available'] = nhAvailable;
      styles[0]['section_nh']['unavailable'] = nhUnavailable;
    }

    // SEAT STYLES
    if (styles && styles[0]['seat']) {
      const seatAvailable: MapViewerStylesState = styles[0]['seat'][
          'available'
        ]! as MapViewerStylesState,
        seatUnavailable: MapViewerStylesState = styles[0]['seat'][
          'unavailable'
        ]! as MapViewerStylesState;

      // AVAILABLE NORMAL
      seatAvailable.normal.none.fillStyle = this.formatRgb(
        this.customStyles['seat-available-none-fill']
      );
      seatAvailable.normal.none.strokeStyle = this.formatRgb(
        this.customStyles['seat-available-none-stroke']
      );
      seatAvailable.normal.none.icon = undefined;

      // AVAILABLE HOVER
      if (seatAvailable.hover && seatAvailable.hover.none) {
        seatAvailable.hover.none.icon = undefined;
      }

      // DISABLED NORMAL
      seatAvailable.normal['disabled'].fillStyle = this.formatRgb(
        this.customStyles['seat-available-disabled-fill']
      );
      seatAvailable.normal['disabled'].strokeStyle = this.formatRgb(
        this.customStyles['seat-available-disabled-stroke']
      );

      // PENDING NORMAL
      seatAvailable.normal['pending'].fillStyle = this.formatRgb(
        this.customStyles['seat-available-pending']
      );
      seatAvailable.normal['pending'].strokeStyle = this.formatRgb(
        this.customStyles['seat-available-pending']
      );

      // UNAVAILABLE NORMAL
      seatUnavailable.normal['none'] = {
        fillStyle: this.formatRgb(this.customStyles['seat-unavailable-none']),
        strokeStyle: this.formatRgb(this.customStyles['seat-unavailable-none']),
        lineWidth: 0.05,
        opacity: 0.5,
      };

      styles[0]['seat']['available'] = seatAvailable;
      styles[0]['seat']['unavailable'] = seatUnavailable;
    }

    this.viewerService.setStyles(styles);
  }

  public setupNeighborSectionNodes(availableSectionIds: string[]): void {
    if (this.demoDataService.isDemoActive()) {
      return;
    }

    const nhNodes = this.viewerService.getNodesByType('section_nh');
    if (!nhNodes.length) {
      return;
    }

    const nhAvailable = nhNodes
      .filter(node => availableSectionIds.includes(node.id))
      .map(node => node.id);

    this.viewerService.setAvailability('section_nh', nhAvailable);
  }

  private setBaseAvailability(availability: string[]): void {
    this.baseAvailability.push(...availability);
  }

  private checkIsTopView(mapId: string): boolean {
    const isTopView = mapId === this.mapViewerLoadOptions.map_id;
    this._isTopView.set(isTopView);
    return isTopView;
  }

  private updateSectionIdTranslation() {
    if (this._isTopView()) {
      const sections = this.viewerService.getNodesByType('section');
      const gaSections = this.viewerService.getNodesByType('general_admission');
      const mergedSections = [...sections, ...gaSections];

      for (const section of mergedSections) {
        if (!this.sectionsMmcIdToTdc[section.original_id]) {
          this.sectionsMmcIdToTdc[section.original_id] = section.id;
        }
        if (!this.sectionsTdcToMmc[section.id]) {
          this.sectionsTdcToMmc[section.id] = section.original_id;
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.viewerSubscriptions.forEach(sub => sub.unsubscribe());
  }
}
